import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const POLL_MS = 15000;
let ctx = null;

export function playPing(low) {
  try {
    ctx = ctx || new (window.AudioContext || window.webkitAudioContext)();
    const notes = low ? [[440, 0], [330, 0.18]] : [[880, 0], [1320, 0.12]];
    notes.forEach(([freq, at]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = low ? "triangle" : "sine";
      o.frequency.value = freq;
      const t = ctx.currentTime + at;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      o.connect(g).connect(ctx.destination);
      o.start(t);
      o.stop(t + 0.4);
    });
  } catch (e) { console.debug("Audio ping unavailable", e); }
}

// Browsers block audio until the user interacts with the page; unlock on first click/key.
function unlockAudio() {
  try { ctx = ctx || new (window.AudioContext || window.webkitAudioContext)(); ctx.resume(); } catch (e) { console.debug("Audio unlock failed", e); }
}

export function useLiveRatings(enabled, onNew) {
  const [muted, setMuted] = useState(() => localStorage.getItem("sv_ping_muted") === "1");
  const mutedRef = useRef(muted);
  const onNewRef = useRef(onNew);
  mutedRef.current = muted;
  onNewRef.current = onNew;

  const toggleMute = () => setMuted((m) => { localStorage.setItem("sv_ping_muted", m ? "0" : "1"); if (m) unlockAudio(); return !m; });

  useEffect(() => {
    const sync = () => setMuted(localStorage.getItem("sv_ping_muted") === "1");
    window.addEventListener("sv-ping-pref", sync);
    return () => window.removeEventListener("sv-ping-pref", sync);
  }, []);

  useEffect(() => {
    window.addEventListener("pointerdown", unlockAudio, { once: true });
    window.addEventListener("keydown", unlockAudio, { once: true });
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let since = null;
    let stopped = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/feedback/live", { params: since ? { since } : {} });
        if (stopped) return;
        since = data.server_time;
        if (!data.items.length) return;
        const items = data.items;
        if (!mutedRef.current) playPing(items.some((x) => x.rating <= 2));
        items.slice(-3).forEach((x) => {
          const who = x.employee_name || "the store";
          const msg = `New ${x.rating}-star rating for ${who}${x.location_name ? ` · ${x.location_name}` : ""}`;
          (x.rating <= 2 ? toast.error : toast.success)(msg, { duration: 6000 });
        });
        if (items.length > 3) toast(`+${items.length - 3} more new ratings`);
        onNewRef.current?.(items);
      } catch (e) { console.warn("Live ratings poll failed; retrying", e); }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { stopped = true; clearInterval(id); };
  }, [enabled]);

  return { muted, toggleMute };
}
